import styled from 'styled-components'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import useApi from '../../../hooks/useApi'
import useTranslation from '../../../hooks/useTranslation'
import useLocale from '../../../hooks/useLocale'
import { base } from '../../../styles/themes.jsx'
import getPeriodDateRange from '../../../utils/widget/getPeriodDateRange.js'
import { ScrollContainer, Grid, HeaderCell, DataRow, Cell, EmptyCell } from './tableGridStyles.jsx'
import { widgetFont } from '../widgetTypography'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${p => p.theme.card.background};
`

const WidgetTitle = styled.h3`
  color: ${p => p.theme.font.color};
  ${widgetFont.title}
  margin: 0;
  padding: ${base.space[4]} ${base.space[4]} 0;
  flex-shrink: 0;
`

const TableWidget = ({ data, labels }) => {
  const navigate = useNavigate()
  const { getMe } = useApi()
  const { t } = useTranslation()
  const { lf } = useLocale()

  const { statsKey, period, columns = [], sort, limit, linkPath } = data || {}
  const dr = getPeriodDateRange(period)
  const from = dr.from
  const to = dr.to

  let url = `stats/?stat=${statsKey}`
  if (from) url += `&from=${from}&to=${to}`

  const { data: rawItems = [], isLoading } = useQuery({
    queryKey: ['stats', statsKey, from, to],
    queryFn: () => getMe(url).then(r => r?.data || []),
    keepPreviousData: true,
    enabled: !!statsKey,
  })

  const formatCell = (value, format) => {
    if (value == null) return '—'
    if (format === 'amount') return lf(value, 2, 2)
    if (format === 'integer') return lf(value, 0, 0)
    if (format === 'number') return lf(value, 2, 2)
    return String(value)
  }

  const processed = useMemo(() => {
    if (!rawItems.length) return []
    let rows = [...rawItems]
    if (sort?.field) {
      rows.sort((a, b) => {
        const av = a[sort.field] ?? 0
        const bv = b[sort.field] ?? 0
        return sort.direction === 'asc' ? (av < bv ? -1 : av > bv ? 1 : 0) : (av > bv ? -1 : av < bv ? 1 : 0)
      })
    }
    if (limit) rows = rows.slice(0, limit)
    return rows
  }, [rawItems, sort, limit])

  const colTemplate = useMemo(() => columns.map(c => c.width || '1fr').join(' '), [columns])
  const isEmpty = !isLoading && processed.length === 0

  return (
    <Container>
      {labels?.title && <WidgetTitle>{t(labels.title)}</WidgetTitle>}
      <ScrollContainer>
        <Grid $cols={colTemplate}>
          {columns.map(col => (
            <HeaderCell key={col.field}>{t(col.label ?? col.labelKey)}</HeaderCell>
          ))}
          {isEmpty ? (
            <EmptyCell>{t(labels?.emptyDescription)}</EmptyCell>
          ) : (
            processed.map((row, ri) => (
              <DataRow key={ri} $clickable={!!linkPath} onClick={linkPath ? () => navigate(`${linkPath}/${row.key}`) : undefined}>
                {columns.map(col => (
                  <Cell key={col.field} data-label={t(col.label ?? col.labelKey)}>{formatCell(row[col.field], col.format)}</Cell>
                ))}
              </DataRow>
            ))
          )}
        </Grid>
      </ScrollContainer>
    </Container>
  )
}

export default TableWidget
